<template>
  <div class="flex flex-col gap-2 relative">
    <NText weight="medium" variant="caption1" class="text-neutral-primary">
      {{ t('modules.order.SLTPConfirmMessage') }}
    </NText>

    <div class="flex flex-col gap-2">
      <div
        class="flex justify-between pt-4 pb-2 border-b border-neutral-secondary"
      >
        <NText
          weight="medium"
          variant="caption1"
          class="text-neutral-secondary-hover"
        >
          {{ t('payment.quantity') }}
        </NText>

        <NText weight="bold" variant="caption1" class="text-neutral-primary">
          {{
            t('modules.order.percentOfAsset', { percent: formModel.percent })
          }}
        </NText>
      </div>

      <div class="flex justify-between pb-2 border-b border-neutral-secondary">
        <NText
          weight="medium"
          variant="caption1"
          class="text-neutral-secondary-hover"
        >
          {{ t('modules.order.profitLimit') }}
        </NText>

        <NumberFormatter #="{ number }" :value="formModel.upperTrigger ?? 0">
          <NText class="text-neutral-primary" variant="caption1" weight="bold">
            {{ formModel.upperTrigger ? number : '-' }}

            <NText
              v-if="formModel.upperTrigger"
              variant="caption1"
              class="text-neutral-tertiary text-left align-middle"
            >
              {{ t('misc.rial') }}
            </NText>
          </NText>
        </NumberFormatter>
      </div>

      <div
        v-if="formModel.upperTrigger"
        class="flex justify-between pb-2 border-b border-neutral-secondary"
      >
        <NText
          weight="medium"
          variant="caption1"
          class="text-neutral-secondary-hover"
        >
          {{ t('modules.order.profitLimitOrderPrice') }}
        </NText>

        <NText weight="bold" variant="caption1" class="text-neutral-primary">
          {{ t('modules.order.priceType.marketPrice.title') }}
        </NText>
      </div>

      <div
        class="flex justify-between pb-2"
        :class="{
          'border-b border-neutral-secondary': formModel.lowerTrigger,
        }"
      >
        <NText
          weight="medium"
          variant="caption1"
          class="text-neutral-secondary-hover"
        >
          {{ t('modules.order.limitOfLoss') }}
        </NText>

        <NumberFormatter #="{ number }" :value="formModel.lowerTrigger ?? 0">
          <NText class="text-neutral-primary" variant="caption1" weight="bold">
            {{ formModel.lowerTrigger ? number : '-' }}

            <NText
              v-if="formModel.lowerTrigger"
              variant="caption1"
              class="text-neutral-tertiary text-left align-middle"
            >
              {{ t('misc.rial') }}
            </NText>
          </NText>
        </NumberFormatter>
      </div>

      <div
        v-if="formModel.lowerTrigger"
        class="flex justify-between pb-2"
        :class="{
          'border-b border-neutral-secondary': formModel.lowerTrigger,
        }"
      >
        <NText
          weight="medium"
          variant="caption1"
          class="text-neutral-secondary-hover"
        >
          {{ t('modules.order.limitOfLossOrderPrice') }}
        </NText>

        <NText weight="bold" variant="caption1" class="text-neutral-primary">
          {{ t('modules.order.priceType.marketPrice.title') }}
        </NText>
      </div>

      <div class="flex justify-between items-center pb-4">
        <NText variant="caption1" weight="medium">
          {{ t('orders.valid_until') }}
        </NText>

        <NText variant="caption1" weight="bold">
          {{
            formModel.validityDate
              ? d(calculatedValidityDate, 'shortDate')
              : t('misc.today')
          }}
        </NText>
      </div>

      <i18n-t
        scope="global"
        :keypath="keyPathMessage"
        class="text-neutral-primary text-c1 font-medium"
        tag="span"
      >
        <template #profitCondition>
          <NumberFormatter #="{ number }" :value="formModel.upperTrigger ?? 0">
            <span class="font-bold">
              {{
                t('modules.order.SLTPProfitCondition', {
                  upperTrigger: number,
                })
              }}
            </span>
          </NumberFormatter>
        </template>

        <template #lossCondition>
          <NumberFormatter #="{ number }" :value="formModel.lowerTrigger ?? 0">
            <span class="font-bold">
              {{
                t('modules.order.SLTPLossCondition', {
                  lowerTrigger: number,
                })
              }}
            </span>
          </NumberFormatter>
        </template>

        <template #percent>
          <span class="font-bold">
            {{ formModel.percent }}
          </span>
        </template>
      </i18n-t>
    </div>

    <div class="flex gap-x-3 mt-4">
      <NButton
        class="w-full"
        size="small"
        :disabled="isLoading"
        @click="handleConfirmOrder"
      >
        {{ t('common.yes') }}
      </NButton>

      <NButton
        variant="plain"
        class="w-full"
        size="small"
        color="gray"
        @click="remove()"
      >
        {{ t('common.refuse') }}
      </NButton>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { NText, useDialog } from '@negar/core';

import NumberFormatter from '@/components/Number.vue';

const { t, d } = useI18n();

const { remove } = useDialog();

const emit = defineEmits<{ (e: 'confirmOrder'): void }>();

type TFormModel = {
  percent: number;
  upperTrigger: number | null;
  lowerTrigger: number | null;
  validityDate: number;
};

type TSLTPConfirmFormDialogProps = {
  isLoading: boolean;
  formModel: TFormModel;
};

const props = defineProps<TSLTPConfirmFormDialogProps>();

const calculatedValidityDate = computed(() => {
  const date = new Date();
  date.setDate(date.getDate() + props.formModel.validityDate);

  return date;
});

const keyPathMessage = computed(() => {
  if (props.formModel.upperTrigger && props.formModel.lowerTrigger) {
    return 'modules.order.SLTPConfirmMessageProfitAndLoss';
  } else if (props.formModel.upperTrigger) {
    return 'modules.order.SLTPConfirmMessageProfit';
  } else {
    return 'modules.order.SLTPConfirmMessageLoss';
  }
});

const handleConfirmOrder = () => {
  emit('confirmOrder');

  remove();
};
</script>
